/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { isEmpty, omit, get } from "lodash"
import { createReducer } from "store/create-reducer"
import {
  CreateReviewReplyRequest,
  CreateReviewReplyResponse,
  UpdateReviewReplyRequest,
  UpdateReviewReplyResponse,
  ListReviewsRequest,
  APIListReviewsResponse,
  FeedbackOverview,
  FeedbackOverviewResponse,
  Review,
  ReviewUnReportableReason,
  ReviewStatus,
  RequestCsvReportResponse,
  GetCsvReportStatusResponse,
} from "types/api/menuFeedback.type"
import { FeedbackReplyState } from "types/models/feedback"
import { Epic, ofType } from "redux-observable"
import { EMPTY, of, concat, timer } from "rxjs"
import { exhaustMap, switchMap, takeUntil } from "rxjs/operators"
import { AppState, EpicDependenciesType } from "store/store.type"
import {
  AuthorizationHeader,
  DownloadCsvEndpoint,
  ReviewEndpoint,
} from "constants/endpoints"
import notification from "components/common/notification"
import { successHttpCode } from "utils/api_helper"
import { TroyWebTracker } from "utils/tracking"
import { storageHelper } from "utils/storage"
import { EventAction, EventCategory } from "constants/tracking"
import dayjs from "dayjs"
import { FeedbackState } from "./feedback.type"
import { getActionSuccess } from "./utils/actionUtils"
import {
  actions as drawerActions,
  actionCreator as drawerActionCreator,
} from "./feedbackDrawer"
import {
  generateFetchReviewsParams,
  generateTempReviewReplies,
  generateRequestCsvBody,
} from "./utils/feedback.utils"

const NAMESPACE = "FEEDBACK"

const CLEAR_REPLY_MESSAGE = `${NAMESPACE}/CLEAR_REPLY_MESSAGE`
const TOUCH_REPLY_MESSAGE = `${NAMESPACE}/TOUCH_REPLY_MESSAGE`
const ACK_UNSAVE_REPLIES = `${NAMESPACE}/ACK_UNSAVE_REPLIES`
const SUBMIT_REPLY_MESSAGE = `${NAMESPACE}/SUBMIT_REPLY_MESSAGE`
const SUBMIT_REPLY_MESSAGE_SUCCESS = `${NAMESPACE}/SUBMIT_REPLY_MESSAGE_SUCCESS`
const SUBMIT_REPLY_MESSAGE_FAILED = `${NAMESPACE}/SUBMIT_REPLY_MESSAGE_FAILED`
const EDIT_REPLY_MESSAGE = `${NAMESPACE}/EDIT_REPLY_MESSAGE`
const EDIT_REPLY_MESSAGE_SUCCESS = `${NAMESPACE}/EDIT_REPLY_MESSAGE_SUCCESS`
const EDIT_REPLY_MESSAGE_FAILED = `${NAMESPACE}/EDIT_REPLY_MESSAGE_FAILED`
const FETCH_REVIEWS = `${NAMESPACE}/FETCH_REVIEWS`
const FETCH_REVIEWS_SUCCESS = `${NAMESPACE}/FETCH_REVIEWS_SUCCESS`
const FETCH_MORE_REVIEWS_SUCCESS = `${NAMESPACE}/FETCH_MORE_REVIEWS_SUCCESS`
const FETCH_REVIEWS_FAILED = `${NAMESPACE}/FETCH_REVIEWS_FAILED`
const FETCH_REVIEW_OVERVIEWS_SUCCESS = `${NAMESPACE}/FETCH_REVIEW_OVERVIEWS_SUCCESS`
const FETCH_REVIEW_OVERVIEWS_FAILED = `${NAMESPACE}/FETCH_REVIEW_OVERVIEWS_FAILED`
const UPDATE_REVIEW = `${NAMESPACE}/UPDATE_REVIEW`
const REQUEST_CSV_REPORT = `${NAMESPACE}/REQUEST_CSV_REPORT`
const REQUEST_CSV_REPORT_SUCCESS = `${NAMESPACE}/REQUEST_CSV_REPORT_SUCCESS`
const REQUEST_CSV_REPORT_FAILED = `${NAMESPACE}/REQUEST_CSV_REPORT_FAILED`
const FETCH_CSV_STATUS = `${NAMESPACE}/FETCH_CSV_STATUS`
const FETCH_CSV_STATUS_SUCCESS = `${NAMESPACE}/FETCH_CSV_STATUS_SUCCESS`
const FETCH_CSV_STATUS_FAILED = `${NAMESPACE}/FETCH_CSV_STATUS_FAILED`
const CANCEL_CSV_REPORT = `${NAMESPACE}/CANCEL_CSV_REPORT`

export const actions = {
  CLEAR_REPLY_MESSAGE,
  TOUCH_REPLY_MESSAGE,
  ACK_UNSAVE_REPLIES,
  SUBMIT_REPLY_MESSAGE,
  SUBMIT_REPLY_MESSAGE_SUCCESS,
  SUBMIT_REPLY_MESSAGE_FAILED,
  EDIT_REPLY_MESSAGE,
  EDIT_REPLY_MESSAGE_SUCCESS,
  EDIT_REPLY_MESSAGE_FAILED,
  FETCH_REVIEWS,
  FETCH_REVIEWS_SUCCESS,
  FETCH_MORE_REVIEWS_SUCCESS,
  FETCH_REVIEWS_FAILED,
  FETCH_REVIEW_OVERVIEWS_SUCCESS,
  FETCH_REVIEW_OVERVIEWS_FAILED,
  UPDATE_REVIEW,
  REQUEST_CSV_REPORT,
  REQUEST_CSV_REPORT_SUCCESS,
  REQUEST_CSV_REPORT_FAILED,
  FETCH_CSV_STATUS,
  FETCH_CSV_STATUS_SUCCESS,
  FETCH_CSV_STATUS_FAILED,
  CANCEL_CSV_REPORT,
}

type ActionTypes =
  | typeof CLEAR_REPLY_MESSAGE
  | typeof TOUCH_REPLY_MESSAGE
  | typeof ACK_UNSAVE_REPLIES
  | typeof SUBMIT_REPLY_MESSAGE
  | typeof SUBMIT_REPLY_MESSAGE_SUCCESS
  | typeof SUBMIT_REPLY_MESSAGE_FAILED
  | typeof EDIT_REPLY_MESSAGE
  | typeof EDIT_REPLY_MESSAGE_SUCCESS
  | typeof EDIT_REPLY_MESSAGE_FAILED
  | typeof FETCH_REVIEWS
  | typeof FETCH_REVIEWS_SUCCESS
  | typeof FETCH_MORE_REVIEWS_SUCCESS
  | typeof FETCH_REVIEWS_FAILED
  | typeof FETCH_REVIEW_OVERVIEWS_SUCCESS
  | typeof FETCH_REVIEW_OVERVIEWS_FAILED
  | typeof UPDATE_REVIEW
  | typeof REQUEST_CSV_REPORT
  | typeof REQUEST_CSV_REPORT_SUCCESS
  | typeof REQUEST_CSV_REPORT_FAILED
  | typeof FETCH_CSV_STATUS
  | typeof FETCH_CSV_STATUS_SUCCESS
  | typeof FETCH_CSV_STATUS_FAILED
  | typeof CANCEL_CSV_REPORT

const clearReplyMessage = () => ({
  type: CLEAR_REPLY_MESSAGE,
})

const touchReplyMessage = (message: string) => ({
  type: TOUCH_REPLY_MESSAGE,
  payload: {
    message,
  },
})

const ackUnsaveReply = () => ({
  type: ACK_UNSAVE_REPLIES,
})

const submitReplyMessage = (payload: {
  body: CreateReviewReplyRequest
  merchantID: string
  form: any
  textMap: { [key: string]: string }
  review?: Review
}) => ({
  type: SUBMIT_REPLY_MESSAGE,
  payload,
})

const submitReplyMessageSuccess = (
  payload: CreateReviewReplyResponse | null,
) => ({
  type: SUBMIT_REPLY_MESSAGE_SUCCESS,
  payload,
})

const submitReplyMessageFailed = ({
  error,
  formError,
}: {
  error?: string
  formError?: string
}) => ({
  type: SUBMIT_REPLY_MESSAGE_FAILED,
  payload: {
    error,
    formError,
  },
})

const editReplyMessage = (payload: {
  body: UpdateReviewReplyRequest
  merchantID: string
  form: any
  textMap: { [key: string]: string }
  review?: Review
}) => ({
  type: EDIT_REPLY_MESSAGE,
  payload,
})

const editReplyMessageSuccess = (
  payload: UpdateReviewReplyResponse | null,
) => ({
  type: EDIT_REPLY_MESSAGE_SUCCESS,
  payload,
})

const editReplyMessageFailed = ({
  error,
  formError,
}: {
  error?: string
  formError?: string
}) => ({
  type: EDIT_REPLY_MESSAGE_FAILED,
  payload: {
    error,
    formError,
  },
})

const fetchReviews = (payload: { body: ListReviewsRequest }) => ({
  type: FETCH_REVIEWS,
  payload,
})

const fetchReviewsSuccess = (payload: APIListReviewsResponse) => ({
  type: FETCH_REVIEWS_SUCCESS,
  payload,
})

const fetchMoreReviewsSuccess = (payload: APIListReviewsResponse) => ({
  type: FETCH_MORE_REVIEWS_SUCCESS,
  payload,
})

const fetchReviewsFailed = () => ({
  type: FETCH_REVIEWS_FAILED,
})

const fetchReviewOverviewsSuccess = (payload: FeedbackOverview) => ({
  type: FETCH_REVIEW_OVERVIEWS_SUCCESS,
  payload,
})

const fetchReviewOverviewsFailed = () => ({
  type: FETCH_REVIEW_OVERVIEWS_FAILED,
})

const updateReview = (reviewID: string, data: Partial<Review>) => ({
  type: UPDATE_REVIEW,
  reviewID,
  data,
})

const requestCsvReport = (payload: { textMap: { [key: string]: string } }) => ({
  type: REQUEST_CSV_REPORT,
  payload,
})

const requestCsvReportSuccess = (payload: RequestCsvReportResponse) => ({
  type: REQUEST_CSV_REPORT_SUCCESS,
  payload,
})

const requestCsvReportFailed = () => ({
  type: REQUEST_CSV_REPORT_FAILED,
})

const fetchCsvStatus = (payload: { textMap: { [key: string]: string } }) => ({
  type: FETCH_CSV_STATUS,
  payload,
})

const fetchCsvStatusSuccess = () => ({
  type: FETCH_CSV_STATUS_SUCCESS,
})

const fetchCsvStatusFailed = () => ({
  type: FETCH_CSV_STATUS_FAILED,
})

const cancelCsvReport = () => ({
  type: CANCEL_CSV_REPORT,
})

export const actionCreator = {
  clearReplyMessage,
  touchReplyMessage,
  ackUnsaveReply,
  submitReplyMessage,
  submitReplyMessageSuccess,
  submitReplyMessageFailed,
  editReplyMessage,
  editReplyMessageSuccess,
  editReplyMessageFailed,
  fetchReviews,
  fetchReviewsSuccess,
  fetchReviewsFailed,
  fetchMoreReviewsSuccess,
  fetchReviewOverviewsSuccess,
  fetchReviewOverviewsFailed,
  updateReview,
  requestCsvReport,
  requestCsvReportSuccess,
  requestCsvReportFailed,
  fetchCsvStatus,
  fetchCsvStatusSuccess,
  fetchCsvStatusFailed,
  cancelCsvReport,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

export const initialState = (): FeedbackState => ({
  replyState: FeedbackReplyState.EMPTY,
  reviews: [],
  loading: false,
  sending: false,
  needToUpdate: false,
  merchantIDs: [],
  isLoadingCsv: false,
})

export const reducer = createReducer<
  FeedbackState,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [CLEAR_REPLY_MESSAGE]: (state: FeedbackState) => ({
    ...state,
    replyState: FeedbackReplyState.EMPTY,
    replyMessage: undefined,
    formError: undefined,
    error: undefined,
  }),
  [TOUCH_REPLY_MESSAGE]: (
    state: FeedbackState,
    action: ActionCreatorTypes["touchReplyMessage"],
  ) => ({
    ...state,
    replyState: FeedbackReplyState.TOUCHED,
    replyMessage: action.payload.message,
  }),
  [ACK_UNSAVE_REPLIES]: (state: FeedbackState) => ({
    ...state,
    replyState: FeedbackReplyState.EMPTY,
  }),
  [UPDATE_REVIEW]: (
    state: FeedbackState,
    action: ActionCreatorTypes["updateReview"],
  ) => {
    // update the attributes of an existing review
    const reviewID = action.reviewID
    const reviews =
      state.reviews?.map(review => {
        return review.reviewID === reviewID
          ? {
              ...review,
              ...(action.data ?? {}),
            }
          : review
      }) ?? []

    return {
      ...state,
      reviews,
    }
  },
  [SUBMIT_REPLY_MESSAGE]: (state: FeedbackState) => ({
    ...state,
    sending: true,
    replyState: FeedbackReplyState.SUBMITTED,
  }),
  [SUBMIT_REPLY_MESSAGE_SUCCESS]: (state: FeedbackState) => ({
    ...state,
    sending: false,
    needToUpdate: false,
    replyState: FeedbackReplyState.EMPTY,
    replyMessage: undefined,
    formError: undefined,
    error: undefined,
  }),
  [SUBMIT_REPLY_MESSAGE_FAILED]: (
    state: FeedbackState,
    action: ActionCreatorTypes["submitReplyMessageFailed"],
  ) => ({
    ...state,
    sending: false,
    replyState: FeedbackReplyState.TOUCHED,
    formError: action.payload.formError,
    error: action.payload.error,
  }),
  [EDIT_REPLY_MESSAGE]: (state: FeedbackState) => ({
    ...state,
    sending: true,
    replyState: FeedbackReplyState.SUBMITTED,
  }),
  [EDIT_REPLY_MESSAGE_SUCCESS]: (state: FeedbackState) => ({
    ...state,
    sending: false,
    needToUpdate: false,
    replyState: FeedbackReplyState.EMPTY,
    replyMessage: undefined,
    formError: undefined,
    error: undefined,
  }),
  [EDIT_REPLY_MESSAGE_FAILED]: (
    state: FeedbackState,
    action: ActionCreatorTypes["editReplyMessageFailed"],
  ) => ({
    ...state,
    sending: false,
    replyState: FeedbackReplyState.TOUCHED,
    formError: action.payload.formError,
    error: action.payload.error,
  }),
  [FETCH_REVIEWS]: (
    state: FeedbackState,
    action: ActionCreatorTypes["fetchReviews"],
  ) => ({
    ...state,
    loading: true,
    needToUpdate: false,
    merchantIDs: action.payload.body.merchantIDs,
    filterState: action.payload.body,
  }),
  [FETCH_REVIEWS_SUCCESS]: (
    state: FeedbackState,
    action: ActionCreatorTypes["fetchReviewsSuccess"],
  ) => ({
    ...state,
    loading: false,
    sending: false,
    reviews: action.payload.reviews || [],
    filterState: {
      ...state.filterState,
      nextToken: isEmpty(action.payload.nextToken)
        ? undefined
        : action.payload.nextToken,
    },
  }),
  [FETCH_MORE_REVIEWS_SUCCESS]: (
    state: FeedbackState,
    action: ActionCreatorTypes["fetchMoreReviewsSuccess"],
  ) => ({
    ...state,
    loading: false,
    sending: false,
    reviews: state.reviews?.concat(action.payload.reviews || []) ?? [],
    filterState: {
      ...state.filterState,
      nextToken: isEmpty(action.payload.nextToken)
        ? undefined
        : action.payload.nextToken,
    },
  }),
  [FETCH_REVIEWS_FAILED]: (state: FeedbackState) => ({
    ...state,
    loading: false,
    reviews: state.filterState?.nextToken ? state.reviews : [], // do not clear if fetching next page
    filterState: {
      ...state.filterState,
      nextToken: undefined, // prevent loop
    },
  }),
  [FETCH_REVIEW_OVERVIEWS_SUCCESS]: (
    state: FeedbackState,
    action: ActionCreatorTypes["fetchReviewOverviewsSuccess"],
  ) => ({
    ...state,
    overview: action.payload,
  }),
  [FETCH_REVIEW_OVERVIEWS_FAILED]: (state: FeedbackState) => ({
    ...state,
    overview: undefined,
  }),
  [getActionSuccess(drawerActions.SUBMIT_REPORT_REVIEW)]: (
    state: FeedbackState,
  ) => ({
    ...state,
    needToUpdate: true,
  }),
  [REQUEST_CSV_REPORT]: (state: FeedbackState) => ({
    ...state,
    isLoadingCsv: true,
    refId: "",
  }),
  [REQUEST_CSV_REPORT_SUCCESS]: (
    state: FeedbackState,
    action: ActionCreatorTypes["requestCsvReportSuccess"],
  ) => ({
    ...state,
    refId: action.payload.ref_id,
  }),
  [REQUEST_CSV_REPORT_FAILED]: (state: FeedbackState) => ({
    ...state,
    isLoadingCsv: false,
  }),
  [FETCH_CSV_STATUS]: (state: FeedbackState) => ({
    ...state,
    isLoadingCsv: true,
  }),
  [FETCH_CSV_STATUS_SUCCESS]: (state: FeedbackState) => ({
    ...state,
    isLoadingCsv: false,
  }),
  [FETCH_CSV_STATUS_FAILED]: (state: FeedbackState) => ({
    ...state,
    isLoadingCsv: false,
  }),
  [CANCEL_CSV_REPORT]: (state: FeedbackState) => ({
    ...state,
    isLoadingCsv: false,
    refId: "",
  }),
})

// epic
const submitReplyEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _store$, { foodMerchantHttpRequest }) =>
  action$.pipe(
    ofType(SUBMIT_REPLY_MESSAGE),
    switchMap((action: ActionCreatorTypes["submitReplyMessage"]) => {
      return foodMerchantHttpRequest
        .POST<CreateReviewReplyResponse>({
          endpoint: ReviewEndpoint.reviewReplyV1,
          data: action.payload.body,
          headers: {
            merchantid: action.payload.merchantID,
            requestsource: "troyPortal",
          },
        })
        .pipe(
          switchMap(res => {
            if (
              isEmpty(res.data?.message) &&
              successHttpCode.includes(res.httpCode)
            ) {
              action.payload.form.resetFields()
              notification.success({
                message: action.payload?.textMap?.submittedTitle || "",
                description: action.payload?.textMap?.submittedBody || "",
              })

              TroyWebTracker.sendEvent({
                category: EventCategory.feedbackHomePage,
                action: EventAction.replySuccessful,
                labels: {
                  REVIEW_ID: action.payload.review?.reviewID,
                  REVIEW_RATING: action.payload.review?.rating,
                  REPLY_MESSAGE: action.payload.body.description,
                },
              })

              return concat(
                of(submitReplyMessageSuccess(res.data)),
                of(drawerActionCreator.toggleReply({ visible: false })),
                of(
                  updateReview(action.payload.review?.reviewID ?? "", {
                    reviewReplies: generateTempReviewReplies(
                      action.payload.body,
                    ),
                  }),
                ),
              )
            }

            notification.error({
              message: action.payload?.textMap?.errorSubmittedTitle || "",
              description: action.payload?.textMap?.errorSubmittedBody || "",
            })

            TroyWebTracker.sendEvent({
              category: EventCategory.feedbackHomePage,
              action: EventAction.replyUnsuccessful,
              labels: {
                REVIEW_ID: action.payload.review?.reviewID,
                REVIEW_RATING: action.payload.review?.rating,
                REPLY_MESSAGE: action.payload.body.description,
              },
            })

            return of(
              submitReplyMessageFailed({
                formError: res.data?.message,
              }),
            )
          }),
        )
    }),
  )

const editReplyEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _store$, { foodMerchantHttpRequest }) =>
  action$.pipe(
    ofType(EDIT_REPLY_MESSAGE),
    switchMap((action: ActionCreatorTypes["editReplyMessage"]) => {
      return foodMerchantHttpRequest
        .PUT<UpdateReviewReplyResponse>({
          endpoint: ReviewEndpoint.reviewReplyV1,
          data: action.payload.body,
          headers: {
            merchantid: action.payload.merchantID,
            requestsource: "troyPortal",
          },
        })
        .pipe(
          switchMap(res => {
            if (
              isEmpty(res.data?.message) &&
              successHttpCode.includes(res.httpCode)
            ) {
              action.payload.form.resetFields()
              notification.success({
                message: action.payload?.textMap?.editedTitle || "",
                description: action.payload?.textMap?.editedBody || "",
              })

              TroyWebTracker.sendEvent({
                category: EventCategory.feedbackHomePage,
                action: EventAction.editSuccessful,
                labels: {
                  REVIEW_ID: action.payload.review?.reviewID,
                  REVIEW_RATING: action.payload.review?.rating,
                  REPLY_MESSAGE: action.payload.body.description,
                },
              })

              return concat(
                of(editReplyMessageSuccess(res.data)),
                of(drawerActionCreator.toggleReply({ visible: false })),
                of(
                  updateReview(action.payload.review?.reviewID ?? "", {
                    reviewReplies: generateTempReviewReplies(
                      action.payload.body,
                    ),
                  }),
                ),
              )
            }

            notification.error({
              message: action.payload?.textMap?.errorEditedTitle || "",
              description: action.payload?.textMap?.errorEditedBody || "",
            })

            TroyWebTracker.sendEvent({
              category: EventCategory.feedbackHomePage,
              action: EventAction.editUnsuccessful,
              labels: {
                REVIEW_ID: action.payload.review?.reviewID,
                REVIEW_RATING: action.payload.review?.rating,
                REPLY_MESSAGE: action.payload.body.description,
              },
            })

            return of(
              editReplyMessageFailed({
                formError: res.data?.message,
              }),
            )
          }),
        )
    }),
  )

const fetchReviewsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _store$, { foodMerchantHttpRequest }) =>
  action$.pipe(
    ofType(FETCH_REVIEWS),
    switchMap((action: ActionCreatorTypes["fetchReviews"]) => {
      const { headers, params: data } = generateFetchReviewsParams(
        action.payload.body,
        _store$.value,
      )
      if (isEmpty(headers?.merchantid)) return of(fetchReviewsFailed()) // unset loading state
      // convert to POST request
      const request = {
        headers,
        data,
        endpoint: ReviewEndpoint.getReviewsV1,
      }
      return foodMerchantHttpRequest.POST<APIListReviewsResponse>(request).pipe(
        switchMap(res => {
          if (res.data !== null) {
            if (action.payload.body.nextToken) {
              return of(fetchMoreReviewsSuccess(res.data))
            }
            return of(fetchReviewsSuccess(res.data))
          }
          return of(fetchReviewsFailed())
        }),
      )
    }),
  )

const fetchReviewsOverviewEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _store$, { foodMerchantHttpRequest }) =>
  action$.pipe(
    // FETCH_REVIEWS event is shared since always need to refetch overview on each listing update
    ofType(FETCH_REVIEWS),
    switchMap((action: ActionCreatorTypes["fetchReviews"]) => {
      if (action.payload.body.nextToken) return EMPTY // no need to update overview details
      const request = generateFetchReviewsParams(
        action.payload.body,
        _store$.value,
      )
      if (isEmpty(request.headers.merchantid)) return EMPTY
      request.params = omit(request.params, ["pageSize", "nextToken"])
      return foodMerchantHttpRequest
        .GET<FeedbackOverviewResponse>({
          ...request,
          endpoint: ReviewEndpoint.getReviewOverviewsV1,
        })
        .pipe(
          switchMap(res => {
            if (res.data !== null) {
              return of(fetchReviewOverviewsSuccess(res.data.feedbackOverview))
            }
            return of(fetchReviewOverviewsFailed())
          }),
        )
    }),
  )

const submitReportEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = action$ =>
  action$.pipe(
    ofType(getActionSuccess(drawerActions.SUBMIT_REPORT_REVIEW)),
    switchMap(action => {
      const reviewID = get(
        action,
        "meta.previousAction.payload.review.reviewID",
      )
      return concat(
        of(drawerActionCreator.toggleReport({ visible: false })),
        reviewID
          ? of(
              updateReview(reviewID ?? "", {
                status: ReviewStatus.REPORTED,
                unreportableReason:
                  ReviewUnReportableReason.ReviewUnreportableReasonReviewIsReported,
                reportedAt: dayjs().toISOString(),
              }),
            )
          : EMPTY,
      )
    }),
  )

const requestCsvReportEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _store$, { mexInsightsHttpRequest }) =>
  action$.pipe(
    ofType(REQUEST_CSV_REPORT),
    switchMap((action: ActionCreatorTypes["requestCsvReport"]) => {
      const body = generateRequestCsvBody(_store$.value) || {}

      return mexInsightsHttpRequest
        .POST<RequestCsvReportResponse>({
          endpoint: DownloadCsvEndpoint.requestCsvReportV1,
          headers: {
            [AuthorizationHeader.mexInsights]: storageHelper.getToken() || "",
          },
          data: body,
        })
        .pipe(
          switchMap(res => {
            if (res.data && res.data.ref_id) {
              return of(
                requestCsvReportSuccess(res.data),
                fetchCsvStatus({ textMap: action.payload.textMap }),
              )
            }

            notification.warning({
              message:
                action.payload?.textMap?.errorBody ||
                "Please wait a moment before trying again.",
              description:
                action.payload.textMap?.errorHeading ||
                "Couldn't download your CSV file",
            })

            return of(requestCsvReportFailed())
          }),
          takeUntil(action$.pipe(ofType(CANCEL_CSV_REPORT))),
        )
    }),
  )

const fetchCsvStatusEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _store$, { mexInsightsHttpRequest }) =>
  action$.pipe(
    ofType(FETCH_CSV_STATUS),
    switchMap((action: ActionCreatorTypes["fetchCsvStatus"]) =>
      timer(0, 2000).pipe(
        exhaustMap(() => {
          const { refId } = _store$.value.feedback
          if (!refId) return of(fetchCsvStatusFailed())

          return mexInsightsHttpRequest
            .GET<GetCsvReportStatusResponse>({
              endpoint: `${DownloadCsvEndpoint.getCsvStatusV1}/${refId}`,
              headers: {
                [AuthorizationHeader.mexInsights]:
                  storageHelper.getToken() || "",
              },
            })
            .pipe(
              switchMap(res => {
                if (res.data) {
                  const { status } = res.data
                  if (status === "IN_PROGRESS") {
                    return EMPTY
                  }
                  if (status === "SUCCESS" && res.data.s3_url) {
                    window.open(res.data.s3_url, "_self")
                    return of(fetchCsvStatusSuccess())
                  }
                }

                notification.warning({
                  message:
                    action.payload?.textMap?.errorBody ||
                    "Please wait a moment before trying again.",
                  description:
                    action.payload.textMap?.errorHeading ||
                    "Couldn't download your CSV file",
                })

                return of(fetchCsvStatusFailed())
              }),
            )
        }),
        takeUntil(
          action$.pipe(
            ofType(
              FETCH_CSV_STATUS_SUCCESS,
              FETCH_CSV_STATUS_FAILED,
              CANCEL_CSV_REPORT,
            ),
          ),
        ),
      ),
    ),
  )

export const epics = {
  fetchReviewsEpic,
  fetchReviewsOverviewEpic,
  submitReplyEpic,
  editReplyEpic,
  submitReportEpic,
  requestCsvReportEpic,
  fetchCsvStatusEpic,
}
